import { LanguageOutlined } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PetsIcon from "@mui/icons-material/Pets";
import PortraitIcon from "@mui/icons-material/Portrait";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ChipTabs from "../../../../components/ChipsTabs";
import MultiLangForm from "../../../../components/form/MultiLangForm";
import Wrapper from "../../../../components/wrappers/Wrapper";
import { SPECIES_SAVE } from "../../../../enums/ApiPaths";
import { SPECIES_MASTER } from "../../../../enums/QueryKeys";
import useActivation from "../hooks/useActivation";
import useDelete from "../hooks/useDelete";
import usePutSpecies from "../hooks/usePutSpecies";
import BreedSelector from "./BreedSelector";
import IconSelector from "./IconSelector";

import { useMediaQuery, useTheme } from "@mui/material";
import { overflowingTitleSx } from "../../../../enums/Styles";

export default function SingleSpeciesFlip({ species }) {
  const [isFlipped, setIsFlipped] = React.useState(false);
  const { t, i18n } = useTranslation();
  const numOfPets = species?.numOfPets;
  const speciesId = species?.id;
  const [isAccordionExpanded, setIsAccordionExpanded] = React.useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleFlip = () => setIsFlipped((prev) => !prev);

  const deletion = useDelete(speciesId, SPECIES_MASTER);
  const submit = usePutSpecies(SPECIES_SAVE, SPECIES_MASTER);
  const activation = useActivation(speciesId);

  const translations = species?.translations;
  const translatedTitle = translations[i18n.language] || species?.title;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { title: species?.title },
  });

  const submitEdit = async (data) => {
    await submit.submitCallback({ id: species?.id, title: data?.title });
  };

  const handleActivationToggle = async () =>
    await activation.activation(speciesId);
  const handleDeleteSpecies = async () => await deletion.deleteFunc(speciesId);
  const handleAccordionToggle = () =>
    setIsAccordionExpanded(!isAccordionExpanded);

  const tabLabels = ["Translation", "Icons", "Breed"];
  const icons = [<LanguageOutlined />, <PortraitIcon />, <PetsIcon />];
  const tabContent = [
    <MultiLangForm
      id={speciesId}
      btnMsg={"Save"}
      object={species}
      postUrl={SPECIES_SAVE}
      invalidationKeys={SPECIES_MASTER}
    />,
    <IconSelector id={speciesId} />,
    <BreedSelector id={speciesId} />,
  ];

  return (
    <Box mb={2}>
      {isFlipped ? (
        // BACK SIDE (Edit form)
        <form onSubmit={handleSubmit(submitEdit)}>
          <Wrapper noPaper>
            <Controller
              name="title"
              control={control}
              rules={{ required: t("CantBeEmpty") }}
              render={({ field }) => (
                <TextField
                  {...field}
                  key={speciesId}
                  size="small"
                  label={t("Fallback")}
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title ? errors.title.message : ""}
                />
              )}
            />

            <Stack
              direction={isMobile ? "column-reverse" : "row"}
              spacing={1}
              mt={1}
              justifyContent={isMobile ? "space-between" : "flex-end"}
            >
              <IconButton
                onClick={handleFlip}
                color="error"
                title={t("Cancel")}
              >
                <CloseIcon />
              </IconButton>

              <IconButton type="submit" color="info" title={t("Save")}>
                <CheckIcon />
              </IconButton>
            </Stack>
          </Wrapper>
        </form>
      ) : (
        // FRONT SIDE
        <Accordion
          expanded={isAccordionExpanded}
          onChange={handleAccordionToggle}
        >
          <AccordionSummary>
            <Wrapper noPaper>
              <Stack
                direction="row"
                alignItems={isMobile ? "flex-start" : "center"}
                justifyContent="space-between"
                spacing={1}
                width="100%"
              >
                <Stack direction={"row"} spacing={1}>
                  <Typography sx={overflowingTitleSx}>
                    {translatedTitle}
                  </Typography>
                  <Typography color={"gray"}> 
                    {`(${t("Fallback")}: ${species?.title})`}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <IconButton
                    color="error"
                    disabled={numOfPets > 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSpecies();
                    }}
                    title={t("Delete")}
                  >
                    <DeleteIcon />
                  </IconButton>

                  <Switch
                    checked={species?.active}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleActivationToggle();
                    }}
                  />

                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFlip();
                    }}
                    title={t("Edit")}
                  >
                    <EditIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Wrapper>
          </AccordionSummary>

          <AccordionDetails>
            <ChipTabs
              lsKey={"SINGLE_SPECIES_FLIP_LS_KEY"}
              icons={icons}
              tabContent={tabContent}
              tabLabels={tabLabels}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}
